import { motion } from "framer-motion";
import { Coffee } from "lucide-react";
import { BRAND } from "@/styles/brand";

const C = BRAND.colors;

const TOTAL_STAMPS = 10;
const REWARD_TEXT = "Gratis Getränk deiner Wahl!";

export default function StampCard({ stamps = 6, reward = REWARD_TEXT, onInfoClick }) {
  const filledCount = Math.min(stamps, TOTAL_STAMPS);
  const isFull = filledCount >= TOTAL_STAMPS;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative overflow-hidden p-5"
      style={{
        borderRadius: 24,
        background: `linear-gradient(145deg, ${C.bgCard} 0%, #FFF9F0 100%)`,
        boxShadow: BRAND.shadows.md,
        border: `1px solid ${C.border}`,
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-[17px] font-bold"
            style={{ color: C.text, fontFamily: BRAND.typography.heading }}>
            Stempelkarte
          </h3>
          <p className="text-[13px] mt-0.5" style={{ color: C.textMuted }}>
            {isFull ? "Voll! Loes deinen Reward ein!" : `${filledCount}/${TOTAL_STAMPS} Stempel`}
          </p>
        </div>
        <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full"
          style={{ backgroundColor: isFull ? C.orange + "15" : C.green + "10" }}>
          <Coffee size={14} style={{ color: isFull ? C.orange : C.green }} />
          <span className="text-[12px] font-bold"
            style={{ color: isFull ? C.orange : C.green }}>
            {isFull ? "Einlösen!" : `${TOTAL_STAMPS - filledCount} noch`}
          </span>
        </div>
      </div>

      {/* Stamp Grid: 2 rows x 5 cols */}
      <div className="grid grid-cols-5 gap-3 mb-4">
        {Array.from({ length: TOTAL_STAMPS }).map((_, i) => {
          const isFilled = i < filledCount;
          const isLast = i === TOTAL_STAMPS - 1;
          const isNew = i === filledCount - 1 && filledCount > 0;

          return (
            <motion.div
              key={i}
              initial={isNew ? { scale: 0 } : false}
              animate={isNew ? { scale: 1 } : false}
              transition={isNew ? { type: "spring", damping: 12, stiffness: 300, delay: 0.1 } : {}}
              className="aspect-square rounded-full flex items-center justify-center relative"
              style={{
                backgroundColor: isFilled ? C.green + "15" : "transparent",
                border: isFilled ? `2px solid ${C.green}` : `2px dashed ${C.border}`,
              }}
            >
              {isFilled ? (
                <motion.div
                  initial={isNew ? { rotate: -180, opacity: 0 } : false}
                  animate={isNew ? { rotate: 0, opacity: 1 } : false}
                  transition={{ duration: 0.4 }}
                >
                  <Coffee size={20} style={{ color: C.green }} strokeWidth={2.5} />
                </motion.div>
              ) : isLast ? (
                <span className="text-lg">🎁</span>
              ) : (
                <span className="text-[11px] font-medium" style={{ color: C.border }}>
                  {i + 1}
                </span>
              )}

              {/* Glow on new stamp */}
              {isNew && (
                <motion.div
                  className="absolute inset-0 rounded-full"
                  initial={{ boxShadow: `0 0 0 0 ${C.green}40` }}
                  animate={{ boxShadow: [`0 0 0 0 ${C.green}40`, `0 0 12px 6px ${C.green}00`] }}
                  transition={{ duration: 1, repeat: 2 }}
                />
              )}
            </motion.div>
          );
        })}
      </div>

      {/* Progress Bar */}
      <div className="w-full h-2 rounded-full overflow-hidden mb-3"
        style={{ backgroundColor: C.border }}>
        <motion.div
          className="h-full rounded-full"
          initial={{ width: 0 }}
          animate={{ width: `${(filledCount / TOTAL_STAMPS) * 100}%` }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          style={{ background: `linear-gradient(90deg, ${C.green}, ${C.greenLight})` }}
        />
      </div>

      {/* Reward Preview */}
      <div className="flex items-center gap-2 p-3 rounded-xl"
        style={{ backgroundColor: C.bg }}>
        <span className="text-xl">🎉</span>
        <div className="flex-1">
          <p className="text-[12px] font-semibold" style={{ color: C.text }}>
            Bei 10 Stempeln:
          </p>
          <p className="text-[12px]" style={{ color: C.green, fontWeight: 600 }}>
            {reward}
          </p>
        </div>
      </div>

      {/* Endowed Progress Hint (for new users) */}
      {filledCount === 2 && (
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="text-center text-[11px] mt-3"
          style={{ color: C.orange }}>
          2 Stempel geschenkt - du hast schon einen super Start!
        </motion.p>
      )}
    </motion.div>
  );
}
